import React, { useEffect, useState } from "react";
import {
  AllActivity,
  updateProjectActivityPayload,
} from "../../../../model/projectModel";
import { useManageClientContext } from "../../../../context/ManageClientContex";
import { getGetRole } from "../../../../helper/api";
import { USERROLEENUM } from "../../../../helper/constant";

interface ProjectActivityProps {
  isProjectClosed?: boolean;
}

const ProjectActivitie: React.FC<ProjectActivityProps> = ({
  isProjectClosed = false,
}) => {
  const {
    projectDetail,
    updateProjectActivity,
    addProjectActivity,
    editProjectActivity,
    deleteProjectActivity,
    toggleMandatoryActivity,
  } = useManageClientContext();

  const [activityList, setActivityList] = useState<AllActivity[]>([]);
  const [searchActivitiesName, setActivitiesSearchName] = useState<string>("");
  const [selectAll, setSelectAll] = useState<boolean>(false);

  // Add Activity modal state
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [newActivityName, setNewActivityName] = useState<string>("");
  const [isAdding, setIsAdding] = useState<boolean>(false);

  // Edit Activity modal state
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [activityToEdit, setActivityToEdit] = useState<AllActivity | null>(null);
  const [editActivityName, setEditActivityName] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(false);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [activityToDelete, setActivityToDelete] = useState<AllActivity | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Role check
  const role = Number(getGetRole());
  const isAdmin = role === USERROLEENUM.ADMIN;
  const isManager = role === USERROLEENUM.MANAGER;
  const canEdit = isAdmin || isManager;

  useEffect(() => {
    if (projectDetail?.activities) {
      setActivityList([...projectDetail.activities]);
    }
  }, [projectDetail?.id, projectDetail?.activities]);

  // Keep selectAll in sync with activityList
  useEffect(() => {
    const filtered = activityList.filter((item) =>
      item.activity.toLowerCase().includes(searchActivitiesName.toLowerCase())
    );
    if (filtered.length > 0) {
      setSelectAll(filtered.every((a) => a.is_enabled));
    } else {
      setSelectAll(false);
    }
  }, [activityList, searchActivitiesName]);

  const handleSelect = (id: number, checkbox: boolean) => {
    if (isProjectClosed) return;
    if (projectDetail) {
      setActivityList(activityList.map((activity) =>
        activity.version_id === id ? { ...activity, is_enabled: checkbox } : activity
      ));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (isProjectClosed) return;
    setSelectAll(checked);
    setActivityList(activityList.map((activity) => {
      const matchesSearch = activity.activity.toLowerCase().includes(searchActivitiesName.toLowerCase());
      return matchesSearch ? { ...activity, is_enabled: checked } : activity;
    }));
  };

  const submitActivity = async () => {
    if (isProjectClosed) return;
    if (projectDetail && activityList) {
      const differingActivities = activityList.filter((activity) => {
        const correspondingActivity = projectDetail.activities.find(
          (item) => item.version_id === activity.version_id,
        );
        return (
          correspondingActivity &&
          correspondingActivity.is_enabled !== activity.is_enabled
        );
      });

      const activityDataPayload: updateProjectActivityPayload[] =
        differingActivities.map((item) => ({
          is_enabled: item.is_enabled,
          projects_id: projectDetail.id,
          version_id: item.version_id,
          activity_name: "",
        }));

      if (activityDataPayload && activityDataPayload.length > 0) {
        await updateProjectActivity(activityDataPayload);
      }
    }
  };

  const handleAddActivity = async () => {
    if (!newActivityName.trim() || !projectDetail) return;
    setIsAdding(true);
    try {
      await addProjectActivity([{
        projects_id: projectDetail.id,
        version_id: 0,
        is_enabled: true,
        activity_name: newActivityName.trim(),
      }]);
      setNewActivityName("");
      setShowAddModal(false);
    } finally {
      setIsAdding(false);
    }
  };

  const handleOpenEditModal = (activity: AllActivity) => {
    setActivityToEdit(activity);
    setEditActivityName(activity.activity);
    setShowEditModal(true);
  };

  const handleSaveEditActivity = async () => {
    if (!activityToEdit || !editActivityName.trim() || !projectDetail) return;
    setIsEditing(true);
    try {
      await editProjectActivity(
        projectDetail.id,
        activityToEdit.version_id,
        editActivityName.trim()
      );
      setActivityList((prev) =>
        prev.map((a) =>
          a.version_id === activityToEdit.version_id
            ? { ...a, activity: editActivityName.trim() }
            : a
        )
      );
      setShowEditModal(false);
      setActivityToEdit(null);
      setEditActivityName("");
    } catch (error) {
      console.error("Failed to edit activity:", error);
    } finally {
      setIsEditing(false);
    }
  };

  const [mandatoryWarning, setMandatoryWarning] = useState<string | null>(null);

  const handleDeleteClick = (activity: AllActivity) => {
    if (activity.is_mandatory) {
      setMandatoryWarning("You cannot delete this activity as this activity is mandatory");
      return;
    }
    setActivityToDelete(activity);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!activityToDelete || !projectDetail) return;

    setIsDeleting(true);

    try {
      // Call the delete API
      await deleteProjectActivity(projectDetail.id, activityToDelete.version_id);

      // Remove from local list immediately
      setActivityList((prev) =>
        prev.filter((a) => a.version_id !== activityToDelete.version_id)
      );

      // Close the modal and reset state
      setShowDeleteModal(false);
      setActivityToDelete(null);

    } catch (error) {
      console.error("Failed to delete activity:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleMandatory = async (activity: AllActivity) => {
    if (!projectDetail) return;
    await toggleMandatoryActivity(projectDetail.id, activity.version_id);
  };

  const filteredList = activityList.filter((item) =>
    item.activity.toLowerCase().includes(searchActivitiesName.toLowerCase())
  );

  return (
    <div className="max-w-2xl relative overflow-x-auto sm:rounded-lg">
      {/* Search */}
      <div className="relative">
        <input
          type="search"
          id="default-search"
          value={searchActivitiesName}
          onChange={(e) => setActivitiesSearchName(e.target.value)}
          className="block w-full p-3 mb-3 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
          placeholder="Search activity"
        />
      </div>

      {/* ? Show warning message if project is closed */}
      {isProjectClosed && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg dark:bg-yellow-900 dark:border-yellow-700">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            Project has ended. Activities cannot be modified.
          </p>
        </div>
      )}

      {/* Header row with title and buttons */}
      <div className="w-full pl-2">
        <div className="flex justify-between items-center my-3">
          <h2 className="text-lg font-bold">Selected Activities</h2>
          <div className="flex gap-2">
            {/* ADD ACTIVITY BUTTON — Admin & Manager */}
            <button
              type="button"
              disabled={isProjectClosed}
              onClick={() => setShowAddModal(true)}
              className={`text-white font-medium rounded-lg text-sm px-4 py-2 text-center transition-all ${isProjectClosed
                ? "bg-gray-400 cursor-not-allowed opacity-60 dark:bg-gray-600"
                : "bg-green-600 hover:bg-green-700 focus:ring-4 focus:outline-none focus:ring-green-300 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800"
                }`}
              title={isProjectClosed ? "Project has ended." : "Add a new activity"}
            >
              Add Activity
            </button>
            {/* ? SAVE BUTTON */}
            <button
              type="button"
              disabled={isProjectClosed}
              onClick={submitActivity}
              className={`text-white font-medium rounded-lg text-sm px-4 py-2 text-center transition-all ${isProjectClosed
                ? "bg-gray-400 cursor-not-allowed opacity-60 dark:bg-gray-600"
                : "bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                }`}
              title={isProjectClosed ? "Project has ended. Cannot save changes." : ""}
            >
              Save
            </button>
          </div>
        </div>

        <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-6 py-3">Activity</th>
              {isAdmin && (
                <th scope="col" className="px-4 py-3 text-center" title="Mandatory — managers cannot remove mandatory activities">
                  Mandatory
                </th>
              )}
              {/* Select All checkbox header */}
              <th scope="col" className="w-12 px-4 py-3 text-center">
                <div className="flex flex-col items-center gap-1">
                  <span className="text-xs normal-case font-medium text-gray-500 dark:text-gray-400">All</span>
                  <input
                    type="checkbox"
                    disabled={isProjectClosed}
                    checked={selectAll}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600 disabled:cursor-not-allowed"
                    title="Select / Deselect all"
                  />
                </div>
              </th>
              {/* Actions header column */}
              {canEdit && <th scope="col" className="w-24 px-4 py-3 text-center"></th>}
            </tr>
          </thead>
          <tbody>
            {filteredList.map((activity) => (
              <tr
                key={activity.version_id}
                className={`border-b dark:border-gray-700 ${isProjectClosed
                  ? "bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800"
                  : "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-600"
                  }`}
              >
                {/* Activity name */}
                <td className="px-6 py-4 flex items-center gap-2">
                  {activity.activity}
                  {activity.is_mandatory && (
                    <span
                      title="Mandatory activity"
                      className="text-amber-500 text-base leading-none"
                    >*</span>
                  )}
                </td>

                {/* Mandatory toggle — Admin only */}
                {isAdmin && (
                  <td className="px-4 py-4 text-center">
                    <button
                      type="button"
                      onClick={() => handleToggleMandatory(activity)}
                      disabled={isProjectClosed}
                      title={activity.is_mandatory ? "Click to remove mandatory" : "Click to make mandatory"}
                      className={`text-xl leading-none transition-colors disabled:cursor-not-allowed ${activity.is_mandatory
                        ? "text-amber-500 hover:text-amber-300"
                        : "text-gray-300 hover:text-amber-400 dark:text-gray-600 dark:hover:text-amber-400"
                        }`}
                    >
                      ★
                    </button>
                  </td>
                )}

                {/* Checkbox */}
                <td className="w-12 px-4 py-4">
                  <div className="flex items-center justify-center">
                    <input
                      id={`checkbox-table-search-${activity.version_id}`}
                      type="checkbox"
                      disabled={isProjectClosed}
                      className={`w-4 h-4 rounded focus:ring-2 ${isProjectClosed
                        ? "bg-gray-200 border-gray-300 cursor-not-allowed dark:bg-gray-700 dark:border-gray-600"
                        : "bg-gray-100 border-gray-300 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600"
                        }`}
                      checked={activity.is_enabled}
                      onChange={(e) => handleSelect(activity.version_id, e.target.checked)}
                      title={isProjectClosed ? "Project has ended. Cannot modify activities." : ""}
                    />
                    <label
                      htmlFor={`checkbox-table-search-${activity.version_id}`}
                      className="sr-only"
                    >
                      checkbox
                    </label>
                  </div>
                </td>

                {/* Actions: Edit (Pencil) for Admin & Manager, Delete (Trash) for Admin only */}
                {canEdit && (
                  <td className="w-24 px-4 py-4 text-center">
                    <div className="flex items-center justify-center gap-3">
                      {/* Edit Activity Button (Pencil Icon) */}
                      <button
                        type="button"
                        onClick={() => handleOpenEditModal(activity)}
                        disabled={isProjectClosed}
                        title={isProjectClosed ? "Project has ended" : "Edit activity name"}
                        className="text-blue-600 hover:text-blue-800 disabled:text-gray-300 disabled:cursor-not-allowed dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                      >
                        <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>

                      {/* Delete Activity Button (Trash Icon) — Admin & Manager for non-mandatory activities */}
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(activity)}
                          disabled={isProjectClosed || activity.is_mandatory}
                          title={
                            activity.is_mandatory
                              ? "Cannot delete a mandatory activity"
                              : isProjectClosed
                                ? "Project has ended"
                                : "Remove activity from project"
                          }
                          className="text-red-500 hover:text-red-700 disabled:text-gray-300 disabled:cursor-not-allowed dark:text-red-400 dark:hover:text-red-300 dark:disabled:text-gray-600 transition-colors"
                        >
                          <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Add Activity Modal ── */}
      {showAddModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={(e) => { if (e.target === e.currentTarget) setShowAddModal(false); }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Add New Activity</h3>
              <button
                type="button"
                onClick={() => { setShowAddModal(false); setNewActivityName(""); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                aria-label="Close modal"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="mb-5">
              <label htmlFor="new-activity-name" className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Activity Name
              </label>
              <input
                id="new-activity-name"
                type="text"
                value={newActivityName}
                onChange={(e) => setNewActivityName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleAddActivity(); }}
                placeholder="Enter activity name"
                className="block w-full p-3 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowAddModal(false); setNewActivityName(""); }}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600 dark:focus:ring-gray-700 transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddActivity}
                disabled={!newActivityName.trim() || isAdding}
                className="px-5 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:ring-4 focus:outline-none focus:ring-green-300 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              >
                {isAdding ? "Saving..." : "Save Activity"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Activity Modal ── */}
      {showEditModal && activityToEdit && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={(e) => { if (e.target === e.currentTarget) { setShowEditModal(false); setActivityToEdit(null); } }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Edit Activity</h3>
              <button
                type="button"
                onClick={() => { setShowEditModal(false); setActivityToEdit(null); setEditActivityName(""); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                aria-label="Close modal"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="mb-5">
              <label htmlFor="edit-activity-name" className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Activity Name
              </label>
              <input
                id="edit-activity-name"
                type="text"
                value={editActivityName}
                onChange={(e) => setEditActivityName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSaveEditActivity(); }}
                placeholder="Enter activity name"
                className="block w-full p-3 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowEditModal(false); setActivityToEdit(null); setEditActivityName(""); }}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600 dark:focus:ring-gray-700 transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEditActivity}
                disabled={!editActivityName.trim() || isEditing}
                className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              >
                {isEditing ? "Updating..." : "Update Activity"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      {showDeleteModal && activityToDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={(e) => { if (e.target === e.currentTarget) { setShowDeleteModal(false); setActivityToDelete(null); } }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Remove Activity</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to remove <strong>"{activityToDelete.activity}"</strong> from this project?
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowDeleteModal(false); setActivityToDelete(null); }}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600 dark:focus:ring-gray-700 transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-5 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:ring-4 focus:outline-none focus:ring-red-300 dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-800 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              >
                {isDeleting ? "Removing..." : "Yes, Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mandatory Activity Warning Popup */}
      {mandatoryWarning && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) setMandatoryWarning(null);
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6 text-center">
            <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900 mb-4">
              <svg
                className="w-6 h-6 text-amber-600 dark:text-amber-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Action Restricted
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              {mandatoryWarning}
            </p>
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setMandatoryWarning(null)}
                className="px-6 py-2.5 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 focus:ring-4 focus:outline-none focus:ring-amber-300 transition-all"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectActivitie;
